import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../store/authStore';
import {
  createConversation,
  fetchConversations,
  fetchMessageUnreadCount,
  fetchMessages,
  markConversationRead,
  sendMessage,
  type Conversation,
  type Message,
} from '../services/messagesApi';

const POLL_INTERVAL_MS = 5000;

export function useConversations() {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mountedRef = useRef(true);

  const load = useCallback(async () => {
    try {
      const data = await fetchConversations();
      if (mountedRef.current) {
        setConversations(data);
        setIsError(false);
      }
    } catch {
      if (mountedRef.current) setIsError(true);
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    load();
    intervalRef.current = setInterval(load, POLL_INTERVAL_MS);
    return () => {
      mountedRef.current = false;
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [load]);

  const startConversation = useCallback(async (recipientId: number) => {
    const conv = await createConversation(recipientId);
    setConversations((prev) => {
      const exists = prev.find((c) => c.id === conv.id);
      return exists ? prev : [conv, ...prev];
    });
    return conv;
  }, []);

  return { conversations, isLoading, isError, startConversation, refresh: load };
}

export function useConversationMessages(conversationId: number | null) {
  const queryClient = useQueryClient();
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mountedRef = useRef(true);

  const load = useCallback(async () => {
    if (!conversationId) return;
    try {
      const data = await fetchMessages(conversationId, 1);
      if (mountedRef.current) setMessages([...data.results].reverse());
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, [conversationId]);

  useEffect(() => {
    mountedRef.current = true;
    if (!conversationId) {
      setMessages([]);
      return;
    }
    setIsLoading(true);
    load();
    intervalRef.current = setInterval(load, POLL_INTERVAL_MS);
    return () => {
      mountedRef.current = false;
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [conversationId, load]);

  const send = useCallback(
    async (body: string) => {
      if (!conversationId) return;
      const msg = await sendMessage(conversationId, body);
      setMessages((prev) => [...prev, msg]);
      return msg;
    },
    [conversationId]
  );

  const markRead = useCallback(async () => {
    if (!conversationId) return;
    await markConversationRead(conversationId);
    setMessages((prev) => prev.map((m) => (m.is_mine ? m : { ...m, is_read: true })));
    // T2441: števec v glavi se ne osvežuje več vsakih 15 s — povej mu takoj.
    queryClient.invalidateQueries({ queryKey: MESSAGE_UNREAD_COUNT_KEY });
  }, [conversationId, queryClient]);

  return { messages, isLoading, send, markRead, refresh: load };
}

/**
 * T2441: števec neprebranih sporočil v glavi.
 *
 * Do T2441 je bil ročni `setInterval` (15 s) z lastnim nalaganjem ob vsakem
 * priklopu komponente — vsak ponovni priklop glave (in StrictMode) je sprožil
 * nov zahtevek, interval pa je tekel tudi v zavihkih v ozadju. Sporočila
 * nimajo WebSocketa, zato ostane poizvedovanje, a prek TanStack Query: en
 * zahtevek za vse porabnike, svež 30 s, interval 60 s in samo v vidnem
 * zavihku. Branje pogovora števec osveži takoj (`markRead`).
 */
export const MESSAGE_UNREAD_COUNT_KEY = ['messages', 'unread-count'] as const;
export const BADGE_STALE_MS = 30_000;
export const BADGE_POLL_INTERVAL_MS = 60_000;

/**
 * Ključ je vezan na uporabnika (vzorec `['subscription', orgSlug]`):
 * `clearAuth` predpomnilnika poizvedb ne počisti, zato bi naslednji uporabnik
 * na istem zavihku sicer do osvežitve videl tuje število. `markRead`
 * invalidira predpono `MESSAGE_UNREAD_COUNT_KEY`, ki zadene vse uporabnike.
 */
export const messageUnreadCountKey = (userId: number | string | null | undefined) =>
  [...MESSAGE_UNREAD_COUNT_KEY, userId ?? null] as const;

/**
 * T3001: ob 401/403 (npr. sysadmin po izklopu 2FA) anketiranje ustavi.
 * Interval je tekel naprej, poizvedba v stanju napake pa se je sprožila znova
 * ob vsakem priklopu glave — na stagingu 28× 403 v nekaj minutah. Nov poskus
 * pride šele, ko se stanje spremeni: drug uporabnik (nov ključ) ali
 * invalidacija (`markRead`). Druge napake (500, omrežje) anketiranja ne ustavijo.
 */
export function isUnreadCountAuthError(error: unknown): boolean {
  const status = (error as { response?: { status?: number } } | null)?.response?.status;
  return status === 401 || status === 403;
}

export function useMessageUnreadCount() {
  const userId = useAuthStore((s) => s.user?.id);
  const { data } = useQuery({
    queryKey: messageUnreadCountKey(userId),
    queryFn: fetchMessageUnreadCount,
    enabled: userId != null,
    staleTime: BADGE_STALE_MS,
    refetchInterval: (query) =>
      isUnreadCountAuthError(query.state.error) ? false : BADGE_POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
    retry: false,
    // T3001: poizvedba v stanju napake se ob ponovnem priklopu ne sproži znova.
    retryOnMount: false,
  });
  return userId != null ? (data?.count ?? 0) : 0;
}
